import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Scan } from '@/lib/api/v1';
import { Card, EmptyState, Icon, Skeleton, Badge, type IconName } from '@/design-system';
import { formatDate } from '@/lib/format';

const KIND_ICON: Record<string, IconName> = {
  qr: 'qr',
  barcode: 'barcode',
  'label-photo': 'camera',
  'recipe-text': 'edit',
  'recipe-photo': 'image',
  'food-photo': 'utensils',
};

export function ScansPage() {
  const { t: _t } = useTranslation();
  const scansQ = useQuery({ queryKey: ['scans'], queryFn: () => Scan.list({ limit: 50 }) });

  return (
    <div className="grid gap-6 max-w-3xl">
      <header>
        <h1 className="ui-section-title text-3xl">Skanne-historikk</h1>
        <p className="text-sm text-mist">Strekkoder, QR-koder og etikett-foto du har skannet.</p>
      </header>

      {scansQ.isLoading ? (
        <div className="grid gap-2">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={64} />)}</div>
      ) : (scansQ.data?.scans.length ?? 0) === 0 ? (
        <EmptyState icon="scan" title="Ingen skanninger enda" description="Gå til Skann-siden og prøv en strekkode." />
      ) : (
        <ul className="grid gap-2">
          {(scansQ.data!.scans).map((s) => (
            <li key={s.id}>
              <Card className="flex items-center gap-4 p-3">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-candle/10 text-candle">
                  <Icon name={KIND_ICON[s.kind] ?? 'scan'} size={16} />
                </div>
                <div className="grid flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-serif text-base text-parchment">
                      {s.kind === 'barcode' || s.kind === 'qr' ? s.code ?? '—' : 'Etikettbilde'}
                    </span>
                    <Badge tone={s.status === 'ready' ? 'success' : s.status === 'failed' ? 'danger' : 'mist'}>{s.status}</Badge>
                    {s.confidence != null && <Badge tone="gold">{(s.confidence * 100).toFixed(0)}%</Badge>}
                  </div>
                  <div className="text-xs text-mist">
                    {s.kind} · {formatDate(s.createdAt)}
                  </div>
                </div>
                {s.matchedBottleId && (
                  <Link to={`/bottle/${s.matchedBottleId}`} className="text-mist hover:text-candle">
                    <Icon name="arrow-right" size={16} />
                  </Link>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
